import { Dialog, Chip, useTranslations, useNumberFormat, useUserProfilePicture } from "@harea/component";
import moment from "moment";
import { ReactNode } from "react";
import { LogOut, DollarSign, FileText, Badge, Building, Check } from "lucide-react";


const Card = ({
  title,
  icon,
  children,
}: {
  title: ReactNode;
  icon: ReactNode;
  children: ReactNode;
}) => (
  <section className="rounded-xl bg-slate-50 p-5">
    <header className="mb-4 flex items-center gap-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-slate-600 shadow-sm">
        {icon}
      </span>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    </header>
    {children}
  </section>
);

const Item = ({
  label,
  children,
  className = "",
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <dt className="text-xs font-medium text-slate-500">{label}</dt>
    <dd className="mt-1 text-sm font-medium text-slate-900">{children}</dd>
  </div>
);


const DebauchageDetailDialog = ({ open, onClose, debauchage }: Props) => {
  const [DETAILS, PERSONNEL, DEPARTURE_TYPE, EFFECTIVE_DATE, REASON, STATUS, INDEMNITIES, DOCUMENTS, SEVERANCE_PAY, NOTICE_PERIOD, UNPAID_LEAVE, TOTAL, CREATED_AT, RESIGNATION_LETTER, DISMISSAL_PV, SOLDE_DE_TOUT_COMPTE, NO_REASON, NO_DOCUMENTS, RESIGNATION, MISCONDUCT, SERIOUS_MISCONDUCT, CONTRACT_END, NEGOTIATED_RESIGNATION, DRAFT, PENDING, VALIDATED, COMPLETED, MATRICULE, DEPARTMENT] = useTranslations(i18n);
  const profilePictureUrl = useUserProfilePicture();
  const formatCurrency = useNumberFormat({
    style: "currency",
    currency: "MGA",
  });

  const getDepartureTypeLabel = (type?: string) => {
    const labels: Record<string, string> = {
      RESIGNATION: RESIGNATION,
      MISCONDUCT: MISCONDUCT,
      SERIOUS_MISCONDUCT: SERIOUS_MISCONDUCT,
      CONTRACT_END: CONTRACT_END,
      NEGOTIATED_RESIGNATION: NEGOTIATED_RESIGNATION,
    };
    return labels[type || ""] || type || "-";
  };

  const getDepartureTypeColor = (type?: string) => {
    const colors: Record<string, string> = {
      RESIGNATION: "bg-warning",
      MISCONDUCT: "bg-danger",
      SERIOUS_MISCONDUCT: "bg-danger",
      CONTRACT_END: "bg-info",
      NEGOTIATED_RESIGNATION: "bg-success",
    };
    return colors[type || ""] || "bg-gray-400";
  };

  const getStatusLabel = (status?: string) => {
    const labels: Record<string, string> = {
      DRAFT: DRAFT,
      PENDING: PENDING,
      VALIDATED: VALIDATED,
      COMPLETED: COMPLETED,
    };
    return labels[status || ""] || status || "-";
  };

  const getStatusColor = (status?: string) => {
    const colors: Record<string, string> = {
      DRAFT: "bg-gray-400",
      PENDING: "bg-warning",
      VALIDATED: "bg-success",
      COMPLETED: "bg-info",
    };
    return colors[status || ""] || "bg-gray-400";
  };

  if (!debauchage) return null;

  const indemnities = [
    {
      label: SEVERANCE_PAY || "Indemnité de licenciement",
      value: debauchage?.calculatedIndemnities?.severancePay,
    },
    {
      label: NOTICE_PERIOD || "Indemnité de préavis",
      value: debauchage?.calculatedIndemnities?.noticePeriodCompensation,
    },
    {
      label: UNPAID_LEAVE || "Indemnité congés payés",
      value: debauchage?.calculatedIndemnities?.unpaidLeaveCompensation,
    },
  ];

  const documents = [
    { label: RESIGNATION_LETTER || "Lettre de démission", name: debauchage?.resignationLetter?.name },
    { label: DISMISSAL_PV || "PV de licenciement", name: debauchage?.dismissalPV?.name },
    { label: SOLDE_DE_TOUT_COMPTE || "Solde de tout compte", name: debauchage?.soldeDeToutCompte?.name },
  ];

  return (
    <Dialog
      open={open}
      title={DETAILS || "Détails du débauchage"}
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="space-y-4 text-slate-900">

        <section className="rounded-xl bg-slate-50 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <img
                src={profilePictureUrl(debauchage?.personnel?.id || 0)}
                alt={debauchage?.personnel?.fullName}
                className="h-14 w-14 shrink-0 rounded-full bg-slate-200 object-cover shadow-sm"
              />
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  {PERSONNEL || "Employé"}
                </p>
                <p className="truncate text-lg font-semibold leading-tight text-slate-900">
                  {debauchage?.personnel?.fullName || "-"}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Badge className="w-4 h-4" />
                    {MATRICULE}: <span className="font-medium text-slate-700">{debauchage?.personnel?.matricule || "-"}</span>
                  </span>
                  {debauchage?.personnel?.department && (
                    <span className="inline-flex items-center gap-1.5">
                      <Building className="w-4 h-4" />
                      {DEPARTMENT}:{" "}
                      <span className="font-medium text-slate-700">
                        {debauchage.personnel.department.designation || "-"}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Chip
              label={getStatusLabel(debauchage?.status)}
              className={getStatusColor(debauchage?.status)}
            />
          </div>
        </section>

        <Card title={DEPARTURE_TYPE || "Informations de départ"} icon={<LogOut className="w-4 h-4" />}>
          <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
            <Item label={DEPARTURE_TYPE || "Type de départ"}>
              <Chip
                label={getDepartureTypeLabel(debauchage?.departureType)}
                className={getDepartureTypeColor(debauchage?.departureType)}
              />
            </Item>
            <Item label={STATUS || "Statut"}>
              <Chip
                label={getStatusLabel(debauchage?.status)}
                className={getStatusColor(debauchage?.status)}
              />
            </Item>
            <Item label={EFFECTIVE_DATE || "Date effective"}>
              <span className="tabular-nums">
                {debauchage?.effectiveDate ? moment(debauchage.effectiveDate).format("DD/MM/YYYY") : "-"}
              </span>
            </Item>
            <Item label={CREATED_AT || "Date de création"}>
              <span className="tabular-nums">
                {debauchage?.createdAt
                  ? moment(
                      typeof debauchage.createdAt === "number"
                        ? debauchage.createdAt
                        : parseInt(debauchage.createdAt)
                    ).format("DD/MM/YYYY")
                  : "-"}
              </span>
            </Item>
            <Item label={REASON || "Raison"} className="sm:col-span-2">
              <p className="rounded-lg bg-white p-3 font-normal leading-relaxed text-slate-700">
                {debauchage?.reason || NO_REASON || "Non renseignée"}
              </p>
            </Item>
          </dl>
        </Card>

        {/* Indemnités */}
        <Card title={INDEMNITIES || "Indemnités calculées"} icon={<DollarSign className="w-4 h-4" />}>
          <ul className="space-y-2">
            {indemnities.map((row) => (
              <li
                key={String(row.label)}
                className="flex items-center justify-between gap-4 rounded-lg bg-white px-4 py-3"
              >
                <span className="text-sm text-slate-600">{row.label}</span>
                <span className="text-sm font-medium tabular-nums text-slate-900">
                  {row.value ? formatCurrency(row.value) : "-"}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-4 rounded-lg bg-indigo-600 px-4 py-3.5 text-white">
            <span className="text-sm font-semibold">{TOTAL || "Total"}</span>
            <span className="text-lg font-semibold tabular-nums">
              {debauchage?.calculatedIndemnities?.total
                ? formatCurrency(debauchage.calculatedIndemnities.total)
                : "-"}
            </span>
          </div>
        </Card>

        <Card title={DOCUMENTS || "Documents"} icon={<FileText className="w-4 h-4" />}>
          <ul className="space-y-2">
            {documents.map((doc) => (
              <li
                key={String(doc.label)}
                className="flex items-center gap-3 rounded-lg bg-white px-4 py-3"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                    doc.name ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {doc.name ? <Check className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-500">{doc.label}</p>
                  <p
                    className={`truncate text-sm ${
                      doc.name ? "font-medium text-slate-900" : "text-slate-400"
                    }`}
                  >
                    {doc.name || NO_DOCUMENTS || "Aucun document"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </Dialog>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  debauchage?: any;
};

export default DebauchageDetailDialog;

const i18n = [
  "std_details",
  "std_personnel",
  "std_departure_type",
  "std_effective_date",
  "std_reason",
  "std_status",
  "std_indemnities",
  "std_documents",
  "std_severance_pay",
  "std_notice_period",
  "std_unpaid_leave",
  "std_total",
  "std_created_at",
  "std_resignation_letter",
  "std_dismissal_pv",
  "std_solde_de_tout_compte",
  "std_no_reason",
  "std_no_documents",
  "std_resignation",
  "std_misconduct",
  "std_serious_misconduct",
  "std_contract_end",
  "std_negotiated_resignation",
  "std_draft",
  "std_pending",
  "std_validated",
  "std_completed",
  "std_matricule",
  "std_department",
];